import { Intent } from "@blueprintjs/core";
import {
  HexId,
  OrgRole,
  ProjectRole,
  humanReadableOrgRole,
  humanReadableProjectRole,
} from "@hex/common";
import React, { useCallback, useState } from "react";
import styled, { useTheme } from "styled-components";

import { HexButton } from "../../hex-components/HexButton.js";
import { HexDialog } from "../../hex-components/HexDialog.js";
import { HexTextArea } from "../../hex-components/HexInput.js";
import { useDialog } from "../../util/dialogs.js";
import { CharacterCountIndicator } from "../common/CharacterCountIndicator.js";
import { useToaster } from "../common/Toasts.js";

const StyledDialog = styled(HexDialog)`
  && {
    width: 520px;
    max-height: 88vh;

    padding: 0;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
  padding: 20px;
  overflow: auto;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 10px;

  border-top: 1px solid ${({ theme }) => theme.borderColor.MUTED};
`;

const RightActions = styled.div`
  display: flex;
  gap: 10px;
`;

interface ProjectRequestAccessDialogProps {
  accessType: ProjectRole;
  isComponent: boolean;
  hexId: HexId;
  requestAccessCallback: (
    requestedRole: ProjectRole,
    reason: string | null,
  ) => Promise<void>;
  isAskThread?: boolean;
}

export const ProjectRequestAccessDialog: React.ComponentType<ProjectRequestAccessDialogProps> =
  React.memo(function ProjectRequestAccessDialog({
    accessType,
    hexId,
    isAskThread = false,
    isComponent,
    requestAccessCallback,
  }) {
    const requestAccessCallbackWrapper = useCallback(
      async (reason: string | null) => {
        await requestAccessCallback(accessType, reason);
      },
      [accessType, requestAccessCallback],
    );
    return (
      <RequestAccessDialog
        accessString={`${
          isAskThread
            ? "Can view thread"
            : humanReadableProjectRole(accessType, isComponent)
        } permissions.`}
        accessedRequestedFor={hexId}
        dialogType="request-project-access"
        requestAccessCallback={requestAccessCallbackWrapper}
      />
    );
  });

interface OrgRequestAccessDialogProps {
  orgRole: OrgRole;
  requestAccessCallback: (
    orgRole: OrgRole,
    reason: string | null,
  ) => Promise<void>;
}

export const OrgRequestAccessDialog: React.ComponentType<OrgRequestAccessDialogProps> =
  React.memo(function OrgRequestAccessDialog({
    orgRole,
    requestAccessCallback,
  }) {
    const requestAccessCallbackWrapper = useCallback(
      async (reason: string | null) => {
        await requestAccessCallback(orgRole, reason);
      },
      [orgRole, requestAccessCallback],
    );

    return (
      <RequestAccessDialog
        accessString={`the ${humanReadableOrgRole(orgRole)} role`}
        dialogType="request-org-access"
        requestAccessCallback={requestAccessCallbackWrapper}
      />
    );
  });

interface RequestAccessDialogProps {
  accessString: string;
  requestAccessCallback: (reason: string | null) => Promise<void>;
  dialogType: "request-project-access" | "request-org-access";
  accessedRequestedFor?: HexId;
}

const MAX_REASON_LENGTH = 150;

const RequestAccessDialog: React.ComponentType<RequestAccessDialogProps> =
  React.memo(function RequestAccessDialog({
    accessString,
    accessedRequestedFor,
    dialogType,
    requestAccessCallback,
  }: RequestAccessDialogProps) {
    const toaster = useToaster();
    const theme = useTheme();

    const { closeDialog: closeDialog_, isOpen } = useDialog(
      dialogType,
      accessedRequestedFor,
    );

    const closeDialog = useCallback(() => closeDialog_(), [closeDialog_]);

    const [isSaving, setIsSaving] = useState<boolean>(false);

    const [reason, setReason] = useState<string | null>(null);

    const updateMessage = useCallback((evt) => {
      setReason(evt.target.value);
    }, []);

    const submitAccessRequest = useCallback(async () => {
      setIsSaving(true);
      try {
        await requestAccessCallback(reason);
        closeDialog();
        setIsSaving(false);
      } catch (e) {
        console.error(e);
        toaster.show({
          message:
            "Your access request could not be submitted. Please try again.",
          intent: Intent.DANGER,
        });
        setIsSaving(false);
      }
    }, [closeDialog, reason, requestAccessCallback, toaster]);

    return (
      <StyledDialog
        isCloseButtonShown={true}
        isOpen={isOpen}
        title="Request access"
        usePortal={true}
        onClose={closeDialog}
      >
        <Container>
          <div>
            You are requesting{" "}
            <span css={{ fontWeight: theme.fontWeight.MEDIUM }}>
              {accessString}
            </span>
          </div>
          <div>
            <div css={{ marginBottom: 8 }}>
              Message{" "}
              <span css={{ color: theme.fontColor.MUTED, fontStyle: "italic" }}>
                (optional)
              </span>
            </div>
            <HexTextArea
              fill={true}
              maxLength={MAX_REASON_LENGTH}
              placeholder=""
              title="Message (optional)"
              value={reason ?? undefined}
              onChange={updateMessage}
            />
            <div
              css={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <CharacterCountIndicator
                count={reason?.length ?? 0}
                css={{ marginTop: 5 }}
                max={MAX_REASON_LENGTH}
              />
            </div>
          </div>
        </Container>
        <Footer>
          <RightActions>
            <HexButton text="Cancel" onClick={closeDialog} />
            <HexButton
              intent={Intent.PRIMARY}
              loading={isSaving}
              text="Request access"
              onClick={submitAccessRequest}
            />
          </RightActions>
        </Footer>
      </StyledDialog>
    );
  });
